import React, { ComponentType, ReactNode } from 'react';
import Countdown from 'react-countdown';

import classNames from 'classnames';

import { Time } from '@uikit/icons/Time';
import { User } from '@uikit/icons/User';

import {
  REWARD_POOL_DISTRIBUTION_POLICY,
  REWARD_POOL_STATUS,
  RewardPool,
} from '@shared/api/types/rewardPools';
import { renderTimeAfterDays } from '@shared/helpers/react-countdown';
import useRerenderAfterDate from '@shared/hooks/useRerenderAfterDate';

export const isRewardPoolUpcoming = (rewardPool: RewardPool) => {
  return (
    rewardPool.status === REWARD_POOL_STATUS.PENDING && new Date(rewardPool.start_date) > new Date()
  );
};

export const isRewardPoolActive = (rewardPool: RewardPool) => {
  return (
    (rewardPool.status === REWARD_POOL_STATUS.ACTIVE &&
      new Date(rewardPool.end_date) > new Date()) ||
    (rewardPool.status === REWARD_POOL_STATUS.PENDING &&
      new Date() >= new Date(rewardPool.start_date))
  );
};

export const isRewardPoolCompleted = (rewardPool: RewardPool) => {
  return (
    rewardPool.status === REWARD_POOL_STATUS.COMPLETED ||
    (rewardPool.status === REWARD_POOL_STATUS.ACTIVE && new Date() > new Date(rewardPool.end_date))
  );
};

const getRewardPoolParticipationTagConfig = (
  rewardPool: RewardPool,
): {
  label: ReactNode;
  Icon: ComponentType<{ className: string }>;
  iconClassName?: string;
  className?: string;
} => {
  if (isRewardPoolUpcoming(rewardPool)) {
    return {
      label: rewardPool.minor_settings.coming_soon ? (
        'Coming Soon'
      ) : (
        <>
          Starts in{' '}
          <span suppressHydrationWarning className="inline-block min-w-16 text-left">
            <Countdown date={rewardPool.start_date} renderer={renderTimeAfterDays} />
          </span>
        </>
      ),
      iconClassName: 'text-[#1258E2]',
      Icon: Time,
    };
  } else if (isRewardPoolActive(rewardPool)) {
    if (
      rewardPool.distribution_policy === REWARD_POOL_DISTRIBUTION_POLICY.PRO_RATA_KARMA ||
      (rewardPool.distribution_policy === REWARD_POOL_DISTRIBUTION_POLICY.RAFFLE &&
        !rewardPool.participants_max)
    ) {
      return {
        label: (
          <>
            Ends in{' '}
            <span suppressHydrationWarning className="inline-block min-w-16 text-left">
              <Countdown date={rewardPool.end_date} renderer={renderTimeAfterDays} />
            </span>
          </>
        ),
        iconClassName: 'text-[#209734]',
        Icon: Time,
      };
    }

    const availableSlots = (rewardPool.participants_max || 0) - rewardPool.participants_counter;

    return {
      label: `${availableSlots} / ${rewardPool.participants_max} Spots Left`,
      iconClassName: 'text-[#209734]',
      Icon: User,
    };
  }

  return {
    label: 'Ended',
    className: 'text-[#65657B]',
    Icon: Time,
  };
};

interface RewardPoolCardParticipationTagProps {
  rewardPool: RewardPool;
  className?: string;
}

const RewardPoolCardParticipationTag = ({
  rewardPool,
  className,
}: RewardPoolCardParticipationTagProps) => {
  const {
    Icon,
    label,
    className: configClassName,
    iconClassName,
  } = getRewardPoolParticipationTagConfig(rewardPool);

  // NOTE: rerender component because some parts of ui depends on those dates
  useRerenderAfterDate(rewardPool.start_date);
  useRerenderAfterDate(rewardPool.end_date);

  return (
    <p
      className={classNames(
        'rounded-full flex text-xs gap-1.5 items-center font-semibold xl:text-sm text-center text-[#09090B] justify-center min-h-10 px-3 py-2 bg-[#F3F3F7]',
        configClassName,
        className,
      )}
      data-qa="participation_tag"
    >
      <Icon className={classNames('icon-xs', iconClassName)} /> {label}
    </p>
  );
};

export default RewardPoolCardParticipationTag;
